import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import styled from 'styled-components';
import { StringParam, useQueryParam } from 'use-query-params';

import EventPage from '../Event/EventPage';
import { OpaqueOverlay } from '../../components/EventForm/ActionMenu';
import BaseModalPage from '../BaseModalPage';
import { useEventCacheContext } from '../../contexts/EventCacheContext';

import { FullContainer } from 'Common/src/components/styled';
import { ULogApplication, ULogSeverity, ULogTag, logSumoEvent } from 'Common/src/api/SumoLogicApi';

import ColorButton from '../../components/buttons/ColorButton';
import { useUserContext } from '../../contexts/UserContext';
import { updateCohostStatus } from '../../api/ElkEventService';
import {
  TAppElkAttendeeStatus,
  TAppElkCohostRequestStatus,
  TAppElkEventId
} from 'TProtocol/prototypes/events/messages';
import { IUEvent, IUInvitee } from '../../lib/event';
import { useAutoSaveContext } from '../../contexts/AutoSaveContext';
import { findCurrentInvitee } from '../../util/attendee';
import { isUserHost } from '../../lib/attendance';

const CohostEventAddPage = ({ isDraft }: { isDraft?: boolean }) => {
  const { eventId } = useParams();
  const [code] = useQueryParam('code', StringParam);
  const navigate = useNavigate();

  const eventCacheContext = useEventCacheContext();
  const autoSaveContext = useAutoSaveContext();
  const userContext = useUserContext();

  const [submitting, setSubmitting] = useState(false);
  const [event, setEvent] = useState<IUEvent>();
  const [error, setError] = useState<string>();

  useEffect(() => {
    void fetchEventOrDraft();
  }, []);

  useEffect(() => {
    if (event !== undefined) {
      if (isUserHost(event, userContext.id)) {
        navigate(`/event/${eventId}`);
      }
    }
  }, [event]);

  const logUserAction = (action: string) => {
    void logSumoEvent({
      app: ULogApplication.ELK,
      severity: ULogSeverity.DEBUG,
      userId: userContext.id,
      tag: ULogTag.UserAction,
      message: `[EditEventPage] ${action}`
    });
  };

  const fetchEventOrDraft = async () => {
    if (eventId === undefined) {
      return;
    }

    if (isDraft) {
      setEvent(await autoSaveContext.fetchDraft(eventId));
    } else {
      setEvent(await eventCacheContext.fetchEvent({ eventId }));
    }
  };

  const updateStatus = async (invitee: IUInvitee, status: TAppElkCohostRequestStatus) => {
    if (eventId !== undefined && code !== null) {
      logUserAction(`Updating cohost status to ${status}`);
      const response = await updateCohostStatus(userContext, new TAppElkEventId({ eventId }),
        invitee.toTAppElkInvitee(),
        status, code);
      if (response.invalidLink) {
        if (code !== undefined) {
          logUserAction(`Invalid cohost link: code = ${code}`);
          setError('This link is an invalid cohost link. Please contact the user who sent you this link.');
        } else {
          logUserAction('Invalid cohost link: missing code');
          setError('A valid cohost link is required.');
        }
        return;
      } else if (response.invalidPermissions) {
        logUserAction('Invalid permission');
        setError('Invalid permission to become cohost.');
        return;
      }
      logUserAction('Update successful');

      await eventCacheContext.fetchEvent({ eventId, force: true });
    }

    navigate(`/event/${eventId}`);
  };

  const onAcceptClick = async () => {
    setSubmitting(true);

    const invitee = findCurrentInvitee(
      undefined,
      userContext.id,
      event,
      userContext.name,
      true,
      userContext.phones,
      userContext.emails
    );
    invitee.rsvpStatus = TAppElkAttendeeStatus.YES;

    await updateStatus(invitee, TAppElkCohostRequestStatus.ACCEPTED);
  };

  const onDeclineClick = async () => {
    setSubmitting(true);

    const invitee = findCurrentInvitee(
      undefined,
      userContext.id,
      event,
      userContext.name,
      true,
      userContext.phones,
      userContext.emails
    );

    await updateStatus(invitee, TAppElkCohostRequestStatus.DECLINED);
  };

  const goBack = () => {
    navigate(`/event/${eventId}`);
  };

  return <FullContainer>
    <BackgroundEventPageWrapper>
      <EventPage eventIdProp={eventId}/>
    </BackgroundEventPageWrapper>
    <OpaqueOverlay/>
    {event &&
      (error ? <BaseModalPage title="Error"
                              subtitle={error}
                              wide={true}>
          <Container>
            <ColorButton type="button" onClick={goBack} noColor={true}>Okay</ColorButton>
          </Container>
        </BaseModalPage> :
        <BaseModalPage title="Become a cohost"
                       subtitle={
                         <span>{event?.hostedBy ?? 'The host'} has invited you to become a cohost for the party <strong>{event?.title}</strong>.</span>}
                       wide={true}>
          <Container>
            <ColorButton type="button" onClick={onAcceptClick}
                         disabled={submitting}>Accept</ColorButton>
            <ColorButton type="button" onClick={onDeclineClick} noColor={true} disabled={submitting}>Decline</ColorButton>
          </Container>
        </BaseModalPage>)
    }
  </FullContainer>;
};

const Container = styled.div`
  margin: 10px 20px 0;
  display: flex;
  flex-direction: column;
  gap: 15px;
`;

const BackgroundEventPageWrapper = styled.div`
  top: 0;
  left: 0;
  position: absolute;
  z-index: 0;
  width: 100%;
  height: 100%;
  overflow: hidden;
`;

export default CohostEventAddPage;
